import { useProjectLocalStore } from "./use-project-local-store";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PosterProject } from "PosterEditor/model/poster-project";
import { useCallback, useRef } from "react";
import { useProjectRemoteStore } from "./use-project-remote-store";
import { useAuth } from "BEDROCK/auth/supabase-auth";
import { useCheckout } from "BEDROCK/pages/checkout/checkout-provider";
import { v4 as uuid } from "uuid";

export const useProjectStore = () => {
  const queryClient = useQueryClient();
  const { isUserAuthenticated } = useAuth();
  const { addProject: addProjectToCheckout } = useCheckout();
  const {
    loadProjects: loadProjectsLocal,
    loadProject: loadProjectLocal,
    saveProject: saveProjectLocal,
    createProject: createProjectLocal,
    deleteProject: deleteProjectLocal,
    deleteAllProjects: deleteAllProjectsLocal,
    draftProject,
    deleteDraft,
  } = useProjectLocalStore();
  const {
    save: saveRemote,
    fetchAllProjects: fetchRemote,
    remove: removeRemote,
  } = useProjectRemoteStore();

  const saveCooldownRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: projects,
    refetch: refetchProjects,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      if (isUserAuthenticated) {
        const remoteData = await fetchRemote();
        if (remoteData) {
          clearLocalCache();
          remoteData.forEach((remoteProject) => {
            saveProjectLocal(remoteProject);
          });
        }
      }
      const data = await loadProjectsLocal();
      return data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const loadProjects = (projectIDs: string[]) => {
    return projectIDs
      .map((projectId) => {
        return loadProjectLocal(projectId);
      })
      .filter((project) => project !== undefined) as PosterProject[];
  };

  const loadProject = (projectId: string) => {
    const project = loadProjectLocal(projectId);
    return project;
  };

  //TODO: proper implementation when backend is up.
  const fetchProject = async (id: string) => {
    return queryClient.fetchQuery({
      queryKey: ["project", id],
      queryFn: async () => await loadProjectLocal(id),
      staleTime: 1000 * 60 * 5,
    });
  };

  //TODO: proper implementation when backend is up.
  const putProject = useMutation({
    mutationFn: async (project: PosterProject) =>
      await saveRemote.mutateAsync(project),
  });

  const saveProject = useCallback(
    (project: PosterProject, forceImmediateRemoteStore?: boolean) => {
      const updated = { ...project, updatedAt: new Date() };
      saveProjectLocal(updated);
      queryClient.setQueryData(["projects"], (oldData?: PosterProject[]) => {
        if (!oldData) return [updated]; // If no data, initialize with this project
        return oldData.map((p) => (p.id === updated.id ? updated : p));
      });
      if (!isUserAuthenticated) {
        return;
      }
      if (!project.isTemplateDraft) {
        if (forceImmediateRemoteStore) {
          saveRemote.mutate(updated);
        }
        if (saveCooldownRef.current) {
          clearTimeout(saveCooldownRef.current);
        }
        saveCooldownRef.current = setTimeout(() => {
          saveRemote.mutate(updated);
          saveCooldownRef.current = null;
        }, 10000);
      }
    },
    [putProject, queryClient, saveProjectLocal]
  );

  const createProject = useMutation({
    mutationFn: async () => {
      const project = await createProjectLocal();
      await putProject.mutateAsync(project);
      return project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const deleteProject = useMutation({
    mutationFn: async (project: PosterProject) => {
      await deleteProjectLocal(project);
      await removeRemote.mutate(project.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const clearLocalCache = () => {
    deleteAllProjectsLocal();
  };

  const saveGuestProjectToUsersAccount = () => {
    if (!isUserAuthenticated) {
      return;
    }
    const project = loadProject("guest_project");

    if (project) {
      const updated = { ...project, id: uuid(), isTemplateDraft: false };
      deleteProjectLocal(project);
      saveProject(updated, true);
      addProjectToCheckout(updated.id);
      return updated.id;
    }
    return;
  };

  return {
    // Cached projects list.
    // This is populated from remote store whn this hook is mounted.
    projects,

    // Loads single project from local storage synchronously.
    // Use if you're sure the project is fetched with fetchProject.
    loadProject,

    // Loads multiple projects from local storage synchronously.
    // Use if you're sure the projects are fetched with fetchProject.
    loadProjects,

    // Loads single projct from remote and saves it locally.
    fetchProject,
    // Saves the project locally.
    saveProject,
    // Saves the project to remote asynchronously.
    putProject,
    // Async create function.
    createProject,
    // Async delete function.
    deleteProject,
    // Manual refresh trigger
    refetchProjects,

    draftProject,
    deleteDraft,

    clearLocalCache,

    saveGuestProjectToUsersAccount,

    isLoading,
    isFetching,
    isError,
  };
};
